"use client";
export const dynamic = "force-dynamic";

import { useRouter } from "next/navigation";
import Link from "next/link";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Loader2,
  KeyRound,
  AlertCircle,
  RefreshCw,
  Check,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import AuthShell from "@/components/auth/AuthShell";
import {
  isValidMobile,
  maskMobile,
  normalizeMobile,
  toEnglishDigits,
  toPersianDigits,
  uuidv4,
} from "@/lib/auth/utils";
import {
  captureMarketingFromUrl,
  clearAuthRedirect,
  getAuthRedirect,
  getFlow,
  setAuthRedirect,
  setFlow,
} from "@/lib/auth/store";
import { requestOtp } from "@/services/auth";
import { ApiError } from "@/lib/api/error-handler";
import { useAuth } from "@/lib/auth/auth-context";

type Status = "idle" | "loading" | "timeout" | "rate" | "error";

export default function PhonePage() {
  const navigate = useRouter();
  const { status: authStatus } = useAuth();
  const [phone, setPhone] = useState(() =>
    typeof window !== "undefined" ? (getFlow().mobile ?? "") : "",
  );
  const [touched, setTouched] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [retryAfter, setRetryAfter] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    captureMarketingFromUrl(window.location.search);
    const redirect = new URLSearchParams(window.location.search).get("redirect");
    if (redirect) setAuthRedirect(redirect);
    else clearAuthRedirect();
  }, []);

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    const target = getAuthRedirect() ?? "/dashboard";
    clearAuthRedirect();
    navigate.replace(target);
  }, [authStatus, navigate]);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const t = setInterval(() => setRetryAfter((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [retryAfter]);

  const valid = isValidMobile(phone);
  const showFieldError = touched && !valid && phone.length > 0;

  if (authStatus !== "unauthenticated") {
    return null;
  }

  function formatDisplay(v: string) {
    const digits = toEnglishDigits(v).replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 4) return toPersianDigits(digits);
    if (digits.length <= 7) return toPersianDigits(`${digits.slice(0, 4)} ${digits.slice(4)}`);
    return toPersianDigits(`${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`);
  }

  function onChange(v: string) {
    const normalized = normalizeMobile(v);
    setPhone(normalized);
    setErrorMsg("");
    setStatus((s) => (s === "rate" || s === "error" || s === "timeout" ? "idle" : s));
    if (isValidMobile(normalized)) {
      void submit(undefined, normalized);
    }
  }

  async function submit(e?: React.FormEvent, mobileOverride?: string) {
    e?.preventDefault();
    setTouched(true);
    const target = mobileOverride ?? phone;
    if (!isValidMobile(target) || status === "loading" || retryAfter > 0) return;
    setStatus("loading");
    setErrorMsg("");

    let result: Awaited<ReturnType<typeof requestOtp>>;
    try {
      result = await requestOtp(target, uuidv4());
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setStatus("rate");
          setRetryAfter(err.retryAfter ?? 120);
          setErrorMsg("تعداد درخواست‌های ارسال کد بیش از حد مجاز است. کمی بعد دوباره تلاش کنید.");
        } else {
          setStatus("error");
          setErrorMsg(err.message || "خطا رخ داد. بعداً دوباره تلاش کنید.");
        }
      } else {
        setStatus("timeout");
        setErrorMsg("اتصال با سرور برقرار نشد.");
      }
      return;
    }

    setFlow({ mobile: target, otpRequestedAt: Date.now(), isNewUser: result.is_new_user });
    setStatus("idle");
    toast.success("کد تأیید ارسال شد", {
      description: (
        <>
          کد ۴ رقمی به{" "}
          <span dir="ltr" className="font-bold tracking-wide">
            {maskMobile(target)}
          </span>{" "}
          پیامک شد.
        </>
      ),
    });
    navigate.push("/auth/verify");
  }

  return (
    <AuthShell
      step={1}
      totalSteps={2}
      eyebrow="شروع به‌کار رایگان"
      heading={
        <>
          خوش آمدید 👋
          <br />
          <span className="brand-gradient-text">با موبایل وارد شوید</span>
        </>
      }
      subheading="شماره خود را وارد کنید تا کد تأیید برایتان پیامک شود. اگر کاربر جدید هستید، حساب در همین مرحله ساخته می‌شود."
    >
      <form onSubmit={submit} className="space-y-5" noValidate>
        {/* Phone input — bold focal field */}
        <div>
          <label htmlFor="mobile" className="mb-2 flex items-center justify-between">
            <span className="text-xs font-semibold text-foreground">شماره موبایل</span>
            {valid && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-500">
                <Check className="h-3 w-3" /> معتبر
              </span>
            )}
          </label>
          <div
            dir="ltr"
            className={`group relative flex items-stretch overflow-hidden rounded-2xl border-2 bg-background transition-all ${
              showFieldError
                ? "border-rose-500/70 shadow-[0_0_0_4px_rgb(244_63_94/0.08)]"
                : valid
                  ? "border-primary/60 shadow-[0_0_0_4px_rgb(0_150_136/0.10)]"
                  : "border-border focus-within:border-primary focus-within:shadow-[0_0_0_4px_rgb(0_150_136/0.10)]"
            }`}
          >
            <div className="grid flex-shrink-0 w-11 place-items-center text-muted-foreground">
              <Smartphone className="h-4 w-4" />
            </div>
            <input
              id="mobile"
              inputMode="tel"
              autoComplete="tel"
              aria-label="شماره موبایل"
              aria-invalid={showFieldError}
              placeholder="۰۹۱۲ ۳۴۵ ۶۷۸۹"
              value={formatDisplay(phone)}
              onChange={(e) => onChange(toEnglishDigits(e.target.value))}
              onBlur={() => setTouched(true)}
              dir="ltr"
              className="h-14 flex-1 bg-transparent px-3 text-left text-xl font-extrabold tracking-[0.08em] text-foreground placeholder:font-medium placeholder:tracking-normal placeholder:text-muted-foreground/40 focus:outline-none"
              maxLength={14}
            />
          </div>
          {showFieldError ? (
            <p role="alert" className="mt-2 flex items-center gap-1.5 text-xs text-rose-500">
              <AlertCircle className="h-3.5 w-3.5" /> شماره موبایل معتبر نیست.
            </p>
          ) : (
            <p className="mt-2 text-[11px] text-muted-foreground">
              مثال: ۰۹۱۲۱۲۳۴۵۶۷ — کد فعال‌سازی به همین شماره ارسال می‌شود.
            </p>
          )}
        </div>

        {/* Server status box */}
        <AnimatePresence>
          {errorMsg && status !== "idle" && (
            <motion.div
              initial={{ opacity: 0, y: -4, height: 0 }}
              animate={{ opacity: 1, y: 0, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className={`flex items-start gap-2 overflow-hidden rounded-2xl border p-3 text-xs ${
                status === "rate"
                  ? "border-amber-500/30 bg-amber-500/8 text-amber-700 dark:text-amber-300"
                  : status === "timeout"
                    ? "border-sky-500/30 bg-sky-500/8 text-sky-700 dark:text-sky-300"
                    : "border-rose-500/30 bg-rose-500/8 text-rose-700 dark:text-rose-300"
              }`}
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold">{errorMsg}</div>
                {status === "rate" && retryAfter > 0 && (
                  <div className="mt-0.5 text-[11px] opacity-80">
                    {toPersianDigits(retryAfter)} ثانیه باقی‌مانده
                  </div>
                )}
                {status === "timeout" && (
                  <button
                    type="button"
                    onClick={() => submit()}
                    className="mt-2 inline-flex items-center gap-1 rounded-md bg-foreground/10 px-2 py-1 font-semibold hover:bg-foreground/15"
                  >
                    <RefreshCw className="h-3 w-3" /> تلاش مجدد
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Submit — oversized */}
        <button
          type="submit"
          disabled={!valid || status === "loading" || retryAfter > 0}
          className="group relative flex h-14 w-full items-center justify-center gap-2 overflow-hidden rounded-2xl bg-gradient-to-l from-primary via-primary to-primary-400 text-base font-extrabold text-primary-foreground shadow-lg shadow-primary/30 transition hover:shadow-xl hover:shadow-primary/40 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none"
        >
          <span className="absolute inset-0 -translate-x-full bg-gradient-to-l from-transparent via-white/25 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
          {status === "loading" ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" /> در حال ارسال کد…
            </>
          ) : (
            <>
              ارسال کد و ادامه
              <ArrowLeft className="h-5 w-5 transition group-hover:-translate-x-1" />
            </>
          )}
        </button>

        {/* Divider */}
        <div className="relative flex items-center gap-3 text-[11px] text-muted-foreground">
          <div className="h-px flex-1 bg-border" />
          <span>یا روش دیگر</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Password login link */}
        <Link
          href="/auth/password"
          className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-border bg-background text-sm font-bold text-foreground transition hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
        >
          <KeyRound className="h-4 w-4" />
          ورود با رمز عبور
        </Link>

        {/* Tiny terms */}
        <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
          با ادامه، شما{" "}
          <Link href="/terms" className="font-semibold text-primary hover:underline">
            قوانین
          </Link>{" "}
          و{" "}
          <Link href="/privacy" className="font-semibold text-primary hover:underline">
            حریم خصوصی
          </Link>{" "}
          ترندی اسمارت را می‌پذیرید.
        </p>
      </form>
    </AuthShell>
  );
}
